import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { concat, forkJoin, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { ConflictLogFilter, SageIntegrationService } from '../../../shared/services/sage-integration.service';
import { ToastService } from '../../shared/services/toast.service';
import {
  ageLabel,
  buildSyncSteps,
  ENTITY_ROUTE_ORDER,
  entityAccent,
  entityAccentForType,
  entityAccentSoft,
  entityAccentSoftForType,
  entityIcon,
  entityApplyOrder,
  entityIconForType,
  entityLabel,
  entityLabelForType,
  entitySlug,
  entityTypeName,
  finishedGoodSyncTarget,
  getEnabledEntityRoutes,
  inMatchingOrder,
  isRowFromRun,
  isStale,
  issueReasonLabel,
  isSyncDisabledError,
  issueFromConflict,
  issueFromError,
  ItemReportGroup,
  ItemReportingGroupTypeId,
  latestSyncedAt,
  leavesOldSageRecord,
  mergeLiveStatuses,
  pageWindow,
  ProvenanceSyncState,
  provenanceSyncState,
  relativeDayLabel,
  reportGroupType,
  REPORT_GROUP_TYPES,
  reviewGroupKind,
  reviewGroupLabel,
  actionableErrorCopy,
  markUnitSaved,
  SAGE_FIX,
  SAGE_FIX_CLEARS,
  entityTypeNumber,
  SyncTarget,
  syncTargetsFor,
  SageConfirmSelectedResult,
  SageConnectionStatus,
  SageEntityTypeName,
  sageErrorMessageFor,
  SageIssueRow,
  SageItemAdjustment,
  SageItemAdjustmentReason,
  SageActionableError,
  SagePendingReviewEntity,
  SageReconciliationMatch,
  SageReviewGroupKind,
  sageSnapshotName,
  SageSyncErrorCodeCount,
  SageSyncEntityStatus,
  SageSyncErrorCategoryCount,
  SageSyncErrorRow,
  SageSyncProvenance,
  stockAdjustmentStatusName,
  stockCorrectionTargetName,
  SageSyncLog,
  syncDirectionIcon,
  syncDirectionLabel,
  syncDirectionName,
  syncRunEntityLabel,
  syncStatusName,
  syncBaseline,
  SyncBaseline,
  SyncProgress,
  SyncStep,
  syncTriggerLabel,
  toUnitOfMeasureOption,
  UnitOfMeasureOption,
  winningSystemName,
} from '../../../shared/services/sage-integration.model';
import {
  ADD_AS_NEW_TOAST,
  canChange,
  chipCount,
  emptyLinksText,
  filterLinkRows,
  fixChipLabel,
  fixChips,
  FixMatchChip,
  FixMatchesView,
  FIX_SECTIONS,
  fixMatchNoun,
  FixMatchesSection,
  FixMatchType,
  fixSectionLabel,
  linkedRows,
  moveToast,
  needsAttentionByRecord,
  recordLine,
  SageLinkAddRequest,
  SageLinkMoveRequest,
  SageLinkRow,
  typeLabel,
  typePill,
} from '../../../shared/services/sage-fix-matches.model';
import { appliedToast, unitFixApplyIds, unitFixView, UnitFixView } from '../../../shared/services/sage-unit-fix.model';
import { UnitGroupView } from '../../../shared/services/sage-unit-group.model';
import { groupTargets, groupUnit } from '../../../shared/services/sage-wizard.model';
import { FixMatchChangeComponent } from './fix-matches/fix-match-change.component';
import { ErrorFixPanelComponent } from './fixes/error-fix-panel.component';
import { SageErrorFixStore } from './fixes/sage-error-fix.store';
import {
  consoleTourSteps,
  readTourStatus,
  SageTourStatus,
  SageTourStep,
  tourStorageKey,
  writeTourStatus,
} from '../../../shared/services/sage-tour.model';
import { ReviewGroup, SageReviewColumnComponent } from './dashboard/review-column.component';
import {
  ErrorGroup,
  ErrorRecordRow,
  SageErrorsColumnComponent,
} from './dashboard/errors-column.component';
import { SageStockColumnComponent } from './dashboard/stock-column.component';
import { MobileSageConsoleViewComponent } from './mobile-sage-console-view/mobile-sage-console-view.component';
import { SageTourComponent } from './sage-tour/sage-tour.component';

export type ConsoleTab = 'sync' | 'runs' | 'matches' | 'review' | 'errors' | 'conflicts' | 'stock' | 'groups';

// 'external' is a run this page didn't start (scheduled sweep, a 409, a reload mid-run): with no
// baseline there's no telling which log rows are its, so it gets no stepper or card state.
export type SyncState =
  | { kind: 'idle' }
  | { kind: 'all'; baseline: SyncBaseline }
  | { kind: 'single'; entityType: number; baseline: SyncBaseline }
  | { kind: 'external' }
  | { kind: 'complete'; scope: 'all' | 'external' | number; failed: boolean };

const SYNC_IDLE: SyncState = { kind: 'idle' };

export interface TargetRow {
  itemId: number;
  itemName: string;
  itemCode: string;
  categoryName: string;
  componentCount: number;
  blockedComponentCount: number;
  target: 'Bundle' | 'Standalone';
  originalTarget: 'Bundle' | 'Standalone';
  // false only for an item with no choice, saved or default — the displayed default must then
  // still require a Save, or it silently never syncs (SAGE.BDL.012 regression).
  hasSavedTarget: boolean;
  sageItemId: number | null;
  sageBundleId: number | null;
}

@Component({
  selector: 'app-sage-console',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SageReviewColumnComponent,
    SageErrorsColumnComponent,
    SageStockColumnComponent,
    MobileSageConsoleViewComponent,
    SageTourComponent,
    FixMatchChangeComponent,
    ErrorFixPanelComponent,
  ],
  templateUrl: './sage-console.component.html',
  styleUrls: [
    './sage-help-button.shared.css',
    './sage-modal.shared.css',
    './sage-side-panel.shared.css',
    './sage-mockup.shared.css',
    './sage-console.component.css',
  ],
})
export class SageConsoleComponent implements OnInit, OnDestroy {
  fullName = '';
  tenantName = '';

  tourOpen = false;
  tourSteps: SageTourStep[] = [];
  tourOfferHint = false;
  helpPulse = false;
  private tourKey: string | null = null;
  private tourShownThisVisit = false;
  private defaultGroupNeeded = false;
  private entityStatusesLoaded = false;

  loading = true;
  loadError = false;

  status: SageConnectionStatus | null = null;
  activeTab: ConsoleTab = 'sync';

  entityStatuses: SageSyncEntityStatus[] = [];
  recentLogs: SageSyncLog[] = [];
  loadingRuns = false;
  runsPage = 1;
  runsTotalCount = 0;

  canScrollEntitiesBack = false;
  canScrollEntitiesForward = false;
  private entityTrackEl: HTMLElement | null = null;
  private entityTrackObserver: ResizeObserver | null = null;

  // ---- Sync dashboard work columns ----

  reviewGroups: ReviewGroup[] = [];
  loadingReviewGroups = false;

  errorGroups: ErrorGroup[] = [];
  /** Errors outside the actionable set — connection, rate_limited and friends, which clear by themselves. */
  retryingErrorCount = 0;
  loadingErrorGroups = false;
  /** Actionable error count per entity type, for the entity card badge. */
  actionableByEntity: Record<string, number> = {};

  dashStockRows: SageItemAdjustment[] = [];
  dashStockTotal = 0;
  loadingDashStock = false;

  // Tenant-wide and unfiltered, so a tab badge is honest before its tab has ever been opened.
  conflictBadgeCount = 0;
  stockBadgeCount = 0;

  // ---- Errors tab ----

  errorRows: SageIssueRow[] = [];
  errorSearch = '';
  private readonly errorSearch$ = new Subject<string>();
  private loadedErrorSearch: string | null = null;
  private errorsRequest?: Subscription;
  errorPage = 1;
  loadingErrors = false;

  // Search + reason chips — the list length, so it drives the pager.
  errorFilteredCount = 0;
  // Search only, category deliberately excluded: the endpoint computes totalCount this way so
  // selecting one reason chip doesn't zero the others.
  errorTotalCount = 0;

  errorCategoryCounts: SageSyncErrorCategoryCount[] = [];
  selectedErrorCategories: string[] = [];
  showResolvedErrors = false;

  // ---- Conflicts tab ----

  conflictRows: SageIssueRow[] = [];
  conflictSearch = '';
  private readonly conflictSearch$ = new Subject<string>();
  conflictPage = 1;
  loadingConflicts = false;
  conflictFilteredCount = 0;
  selectedConflictEntityType: SageEntityTypeName | '' = '';

  // ---- Shared by both issue lists ----

  readonly pageSize = 25;
  expandedIssueKey: string | null = null;
  expandedErrorDetail: SageSyncErrorRow | null = null;
  loadingErrorDetail = false;

  reviewEntities: SagePendingReviewEntity[] = [];
  reviewTotalCount = 0;
  applyingReviewSessionId: number | null = null;
  showApplyAllConfirm = false;
  applyingAllReview = false;

  stockAdjustments: SageItemAdjustment[] = [];
  stockReasons: SageItemAdjustmentReason[] = [];
  releasingAdjustment: SageItemAdjustment | null = null;
  selectedReasonId: number | null = null;
  releasing = false;

  targets: TargetRow[] = [];
  // Null until the list has loaded once, so its button shows no count before then.
  finishedGoodCount: number | null = null;
  targetsSearch = '';
  savingTargets = false;
  pendingTargetSwitches: TargetRow[] | null = null;

  openProvenanceItemId: number | null = null;
  provenanceCache: Record<number, SageSyncProvenance | 'loading' | 'error'> = {};
  readonly provenanceSyncState = provenanceSyncState;

  // ---- Fix matches ----

  fixSection: FixMatchesSection = 'Item';
  // The link list last shown; it stays put while Finished goods shows.
  fixType: FixMatchType = 'Item';
  linkRows: SageLinkRow[] = [];
  loadingLinks = false;
  linksLoadError = false;
  linksSearch = '';
  linksChip: FixMatchChip = 'all';
  linkAttention: ReadonlyMap<number, string> = new Map();
  linkUnits: UnitOfMeasureOption[] = [];
  changingLink: SageLinkRow | null = null;
  savingLink = false;
  linkError: string | null = null;
  // Until the page reloads, so the user can see which rows they've already dealt with.
  fixedLinks: Record<FixMatchType, Set<number>> = { Item: new Set(), Supplier: new Set() };
  linkCounts: Record<FixMatchType, number | null> = { Item: null, Supplier: null };
  readonly linkTypeLabel = typeLabel;
  readonly linkTypePill = typePill;
  readonly linkRecordLine = recordLine;
  readonly fixSections = FIX_SECTIONS;
  readonly fixSectionLabel = fixSectionLabel;
  readonly relativeDayLabel = relativeDayLabel;
  readonly canChangeLink = canChange;
  readonly trackLink = (_: number, row: SageLinkRow) => row.aivorRecordId;

  // ---- Units for Sage items that didn't come in ----

  /** The reason the fix panel is open for; null while it's closed. */
  errorFix: ErrorGroup | null = null;
  /** What the open reason's fix needs, shared by the web panel and the phone sheet (both are rendered). */
  readonly fixStore: SageErrorFixStore;
  private readonly fixStoreSubscriptions = new Subscription();
  /** The sync started from the fix panel: a record type, or 'all'. */
  private fixSyncKey: string | null = null;
  fixSynced = false;
  /** An Apply ran in this units panel, so an empty review means the reason is fixed. */
  private unitsApplied = false;
  unitFix: UnitFixView | null = null;
  private unitFixSessionId: number | null = null;
  private unitFixMatches: SageReconciliationMatch[] = [];
  private unitFixUnits: UnitOfMeasureOption[] = [];
  private unitFixReasons: ReadonlyMap<number, string> = new Map();
  private readonly unitFixSavingIds = new Set<number>();

  menuOpen = false;
  showDisconnectConfirm = false;
  disconnecting = false;

  // Mirrors status.syncEnabled. No longer user-settable from here, but the backend can still report
  // a paused organisation, and a SyncDisabled 409 corrects it mid-session.
  syncEnabled = true;

  // The trigger endpoint returns as soon as the run is queued, so completion has to be watched
  // for separately — see beginSyncWatch.
  syncState: SyncState = SYNC_IDLE;
  syncProgress: SyncProgress = { steps: [], currentIndex: -1, pullPhase: false, anyFailed: false };
  // Outlives the completion hold so a failure can be read and acted on; cleared when that entity
  // next syncs.
  private readonly failedEntities = new Set<number>();
  private syncPollHandle: ReturnType<typeof setInterval> | null = null;
  private syncCompleteHandle: ReturnType<typeof setTimeout> | null = null;
  private sawSyncRunning = false;
  private syncWatchStartedAt = 0;
  private hiddenAt: number | null = null;
  private destroyed = false;
  private readonly searchSubscriptions = new Subscription();
  private readonly SYNC_POLL_MS = 2000;
  private readonly SYNC_START_GRACE_MS = 12000;
  private readonly SYNC_WATCH_TIMEOUT_MS = 5 * 60 * 1000;
  private readonly SYNC_COMPLETE_HOLD_MS = 1500;

  readonly entityLabel = entityLabel;
  readonly entityIcon = entityIcon;
  readonly entityAccent = entityAccent;
  readonly entityAccentSoft = entityAccentSoft;
  readonly entitySlug = entitySlug;
  readonly ageLabel = ageLabel;
  readonly isStale = isStale;
  readonly syncDirectionName = syncDirectionName;
  readonly syncDirectionLabel = syncDirectionLabel;
  readonly syncDirectionIcon = syncDirectionIcon;
  readonly syncRunEntityLabel = syncRunEntityLabel;
  readonly syncTriggerLabel = syncTriggerLabel;
  readonly syncStatusName = syncStatusName;
  readonly stockCorrectionTargetName = stockCorrectionTargetName;
  readonly stockAdjustmentStatusName = stockAdjustmentStatusName;
  readonly winningSystemName = winningSystemName;
  readonly issueReasonLabel = issueReasonLabel;
  readonly conflictEntityOptions = ENTITY_ROUTE_ORDER;

  readonly trackIssue = (_: number, row: SageIssueRow) => row.key;
  // Every poll returns fresh objects; without these the cards and steps are rebuilt every 2s,
  // restarting their animations.
  readonly trackEntity = (_: number, e: SageSyncEntityStatus) => e.entityType;
  readonly trackStep = (_: number, step: SyncStep) => step.entityType;
  // Bound, so the mobile view can call them as inputs; passing the methods themselves would lose `this`.
  readonly cardSyncStateFn = (entityType: number) => this.cardSyncState(entityType);
  readonly actionableCountFn = (entityType: number) => this.actionableCountFor(entityType);
  readonly dependencyPillFn = (row: SagePendingReviewEntity) => this.dependencyPill(row);
  readonly isStockAdjustmentOpenFn = (row: SageItemAdjustment) => this.isStockAdjustmentOpen(row);
  readonly isTargetBlockedFn = (row: TargetRow) => this.isTargetBlocked(row);
  readonly targetBlockedReasonFn = (row: TargetRow) => this.targetBlockedReason(row);
  readonly provenanceForFn = (row: TargetRow) => this.provenanceFor(row);
  readonly provenanceStateForFn = (row: TargetRow) => this.provenanceStateFor(row);
  readonly groupTypeCountFn = (typeId: ItemReportingGroupTypeId | 0) => this.groupTypeCount(typeId);
  readonly isDefaultSalesGroupFn = (group: ItemReportGroup) => this.isDefaultSalesGroup(group);

  // ngTemplateOutlet types its context as Object|null, so under strictTemplates the rows arrive
  // as `any` and everything inside the shared row template silently loses type checking.
  readonly asIssueRows = (rows: unknown) => rows as SageIssueRow[];

  constructor(
    private sageService: SageIntegrationService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
    private zone: NgZone,
    private location: Location
  ) {
    this.fixStore = new SageErrorFixStore(sageService, toast);
    this.fixStoreSubscriptions.add(this.fixStore.errorsChanged$.subscribe(() => this.loadErrorGroups()));
    this.fixStoreSubscriptions.add(this.fixStore.groupsChanged$.subscribe(() => this.loadReportGroups()));
    this.fixStoreSubscriptions.add(this.fixStore.syncDisabled$.subscribe(() => (this.syncEnabled = false)));
  }

  // A setter because the track is recreated each time the Sync dashboard tab is reopened.
  @ViewChild('entityTrack')
  set entityTrack(ref: ElementRef<HTMLElement> | undefined) {
    this.entityTrackObserver?.disconnect();
    this.entityTrackEl = ref?.nativeElement ?? null;
    if (!this.entityTrackEl) return;

    // zone.js doesn't patch ResizeObserver, so the callback has to re-enter the zone itself.
    // Cards rendering after a data load change the track's height, so this covers that case too.
    this.entityTrackObserver ??= new ResizeObserver(() => this.zone.run(() => this.updateEntityScrollState()));
    this.entityTrackObserver.observe(this.entityTrackEl);
  }

  updateEntityScrollState(): void {
    const el = this.entityTrackEl;
    if (!el) {
      this.canScrollEntitiesBack = this.canScrollEntitiesForward = false;
      return;
    }
    this.canScrollEntitiesBack = el.scrollLeft > 1;
    this.canScrollEntitiesForward = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  scrollEntities(direction: 1 | -1): void {
    const el = this.entityTrackEl;
    const card = el?.firstElementChild as HTMLElement | null;
    if (!el || !card) return;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    el.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  ngOnInit(): void {
    this.wireSearchStreams();

    const jwt = localStorage.getItem('authToken');
    if (jwt) {
      try {
        const decoded: any = jwtDecode(jwt);
        this.fullName = decoded.FullName || '';
        this.tenantName = decoded.TenantName || '';
        this.tourKey = tourStorageKey(decoded.TenantId, decoded.UserId);
      } catch {
        /* invalid token — leave fields blank */
      }
    }

    this.sageService.getStatus().subscribe({
      next: (status) => {
        this.status = status;
        this.syncEnabled = status.syncEnabled !== false;
        if (!status.connected) {
          this.router.navigate(['/sage-integration'], { queryParams: { sage: 'disconnected' } });
          return;
        }
        this.loading = false;
        this.watchTabParam();
        this.loadTabBadgeCounts();
        this.loadTab(this.activeTab);
        // The connection card's "Last synced" needs the runs on every tab; the dashboard loads them itself.
        if (this.activeTab !== 'sync') this.loadEntityStatuses();
        this.initialTabLoaded = true;
        this.checkDefaultSalesReportingGroup();
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  private readonly TAB_QUERY_MAP: Record<string, ConsoleTab> = {
    sync: 'sync',
    runs: 'runs',
    matches: 'matches',
    'fix-matches': 'matches',
    review: 'review',
    errors: 'errors',
    conflicts: 'conflicts',
    stock: 'stock',
    // Finished goods was a tab of its own; it's now a section of Fix matches (see watchTabParam).
    items: 'matches',
    groups: 'groups',
    // Errors and Conflicts were briefly one "Sync issues" tab, which wrote ?tab=issues into the
    // URL — the rewrite below makes any link left over from that self-healing.
    issues: 'errors',
  };

  private readonly DRILL_DOWN_TABS: ReadonlySet<ConsoleTab> = new Set<ConsoleTab>(['review', 'errors', 'stock']);
  // The tab a drill-down was opened from, so its back control knows whether popping history lands
  // on the dashboard. Null after a deep link straight into a drill-down.
  private drillDownFrom: ConsoleTab | null = null;
  private initialTabLoaded = false;
  private tabParamSubscription?: Subscription;

  // Follows ?tab= for the life of the page, not just on open: system Back from a drill-down and the
  // mobile top-bar tabs both change the tab only through the URL.
  private watchTabParam(): void {
    this.tabParamSubscription = this.route.queryParamMap.subscribe((params) => {
      const requested = params.get('tab');
      const mapped = requested ? this.TAB_QUERY_MAP[requested] : undefined;
      if (requested && mapped && requested !== mapped) {
        this.writeTabToUrl(mapped, true);
      }

      const tab = mapped ?? 'sync';
      if (!this.DRILL_DOWN_TABS.has(tab)) this.drillDownFrom = null;
      if (requested === 'items') {
        if (this.initialTabLoaded && this.activeTab === 'matches') this.setFixSection('FinishedGood');
        else this.fixSection = 'FinishedGood';
      }
      if (this.initialTabLoaded && tab === this.activeTab) return;
      this.activeTab = tab;
      // On open, ngOnInit loads the tab itself, after the badge counts.
      if (this.initialTabLoaded) this.loadTab(tab);
    });
  }

  private writeTabToUrl(tab: ConsoleTab, replaceUrl: boolean): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.entityTrackObserver?.disconnect();
    this.stopSyncWatch();
    if (this.syncCompleteHandle !== null) clearTimeout(this.syncCompleteHandle);
    this.searchSubscriptions.unsubscribe();
    this.fixStoreSubscriptions.unsubscribe();
    this.tabParamSubscription?.unsubscribe();
  }

  private wireSearchStreams(): void {
    this.searchSubscriptions.add(
      // Compared with what the list last loaded, not the previous keystroke, so typing back to the
      // loaded search doesn't load it again.
      this.errorSearch$.pipe(debounceTime(300), filter((value) => value !== this.loadedErrorSearch)).subscribe((value) => {
        this.errorSearch = value;
        this.errorPage = 1;
        this.collapseIssueRow();
        this.loadErrors();
      })
    );
    this.searchSubscriptions.add(
      this.conflictSearch$.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
        this.conflictSearch = value;
        this.conflictPage = 1;
        this.loadConflicts();
      })
    );
  }

  // Opening a drill-down from a tab pushes a history entry, so system Back returns to that tab.
  // Everything else replaces, so hopping between tabs doesn't pile up history.
  selectTab(tab: ConsoleTab): void {
    const push = this.DRILL_DOWN_TABS.has(tab) && !this.DRILL_DOWN_TABS.has(this.activeTab);
    if (push) {
      this.drillDownFrom = this.activeTab;
    } else if (!this.DRILL_DOWN_TABS.has(tab)) {
      this.drillDownFrom = null;
    }
    this.activeTab = tab;
    this.writeTabToUrl(tab, !push);
    this.loadTab(tab);
  }

  backToDashboard(): void {
    if (this.drillDownFrom === 'sync') {
      this.location.back();
      return;
    }
    // A deep link, or a drill-down opened from another tab: going back wouldn't reach the dashboard.
    this.selectTab('sync');
  }

  private loadEntityStatuses(): void {
    this.sageService.getSyncStatus().subscribe({
      next: (res) => {
        this.entityStatuses = inMatchingOrder(res.entities || []);
        this.entityStatusesLoaded = true;
        // A scheduled sweep may already be under way when the console opens.
        if (res.isSyncing && this.syncState.kind === 'idle') {
          this.syncState = { kind: 'external' };
          this.beginSyncWatch();
        }
        this.maybeStartTour();
        this.maybePromptDefaultGroup();
      },
      error: () => {
        this.entityStatusesLoaded = true;
        this.toast.error('Could not load sync status.');
        this.maybePromptDefaultGroup();
      },
    });
  }

  private loadTab(tab: ConsoleTab): void {
    if (tab === 'sync') this.loadSyncDashboard();
    if (tab === 'runs') {
      this.runsPage = 1;
      this.loadRecentRuns();
    }
    if (tab === 'matches') this.loadFixSection();
    if (tab === 'review') this.loadReview();
    if (tab === 'errors') this.loadErrors();
    if (tab === 'conflicts') this.loadConflicts();
    if (tab === 'stock') this.loadStockAdjustments();
    if (tab === 'groups') this.loadReportGroups();
  }

  // ---- Sync dashboard ----

  private loadSyncDashboard(): void {
    this.loadEntityStatuses();
    this.loadReviewGroups();
    this.loadErrorGroups();
    this.loadDashboardStock();
  }

  // ---- Recent sync runs ----

  private loadRecentRuns(): void {
    this.loadingRuns = true;
    this.sageService.getSyncLogs({ page: this.runsPage, pageSize: this.pageSize }).subscribe({
      next: (res) => {
        this.loadingRuns = false;
        this.recentLogs = res.items || [];
        this.runsTotalCount = res.totalCount;
      },
      error: () => {
        this.loadingRuns = false;
        this.toast.error('Could not load recent sync runs.');
      },
    });
  }

  get runsTotalPages(): number {
    return Math.max(1, Math.ceil(this.runsTotalCount / this.pageSize));
  }

  get runsPageNumbers(): (number | null)[] {
    return pageWindow(this.runsPage, this.runsTotalPages);
  }

  goToRunsPage(page: number): void {
    if (page < 1 || page > this.runsTotalPages || page === this.runsPage || this.loadingRuns) return;
    this.runsPage = page;
    this.loadRecentRuns();
  }

  // ---- Needs review column ----
  //
  // There is no endpoint for the kind-level breakdown, so it is aggregated from each pending
  // entity's session. getSession is used directly, never getOrStartSession: that helper falls back
  // to POST /start on 404, which runs a full Sage reconciliation — one per entity type, on every
  // dashboard load, against a 100 req/min company limit whose breach blocks the IP for an hour.
  private loadReviewGroups(): void {
    this.loadingReviewGroups = true;
    this.sageService.getPendingReview().subscribe({
      next: (res) => {
        this.reviewEntities = res.entities || [];
        this.reviewTotalCount = res.totalCount;
        this.applyReviewCount(res.totalCount);

        const pending = [...new Set(this.reviewEntities.filter((e) => e.reviewCount > 0).map((e) => e.entityType))];
        if (!pending.length) {
          this.reviewGroups = [];
          this.loadingReviewGroups = false;
          return;
        }

        forkJoin(
          pending.map((entityType) =>
            this.sageService.getSession(entityTypeName(entityType)).pipe(
              map((res) => ({ entityType, matches: res.matches || [] })),
              // A 404 just means the session has since been confirmed; degrade to no groups for
              // that entity rather than emptying the whole column.
              catchError(() => of({ entityType, matches: [] }))
            )
          )
        ).subscribe((results) => {
          this.loadingReviewGroups = false;
          this.reviewGroups = this.buildReviewGroups(results);
        });
      },
      error: () => {
        this.loadingReviewGroups = false;
        this.toast.error('Could not load records awaiting review.');
      },
    });
  }

  private buildReviewGroups(
    results: { entityType: number; matches: SageReconciliationMatch[] }[]
  ): ReviewGroup[] {
    const groups = new Map<string, ReviewGroup>();

    for (const { entityType, matches } of results) {
      const label = entityLabel(entityType);
      const typeName = entityTypeName(entityType);

      for (const match of matches) {
        const kind = reviewGroupKind(match.decision);
        const key = `${entityType}:${kind}`;
        let group = groups.get(key);
        if (!group) {
          group = {
            key,
            kind,
            entityType,
            label: reviewGroupLabel(kind, label),
            count: 0,
            examples: [],
            action: kind === 'ready' ? 'Apply' : kind === 'needs-uom' ? 'Choose units' : 'Review',
          };
          groups.set(key, group);
        }
        group.count++;
        if (group.examples.length < 3) {
          const name = sageSnapshotName(match.sageRecordSnapshot, typeName);
          if (name && name !== '—') group.examples.push(name);
        }
      }
    }

    // Undecided work first, then the largest groups — "ready to apply" is the tail of the queue.
    const kindOrder: SageReviewGroupKind[] = [
      'possible-match',
      'needs-uom',
      'new-in-sage',
      'new-in-aivor',
      'ready',
    ];
    return [...groups.values()].sort(
      (a, b) => kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind) || b.count - a.count
    );
  }

  openReviewGroup(group: ReviewGroup): void {
    if (group.kind === 'needs-uom') {
      this.fixErrorGroup(this.errorGroups.find((g) => g.fix === 'units') ?? this.errorGroupFor('NeedsUomMapping', [], group.count));
      return;
    }
    const slug = entitySlug(group.entityType);
    if (!slug) {
      this.selectTab('review');
      return;
    }
    this.router.navigate(['/sage-integration', slug], { queryParams: { from: 'console' } });
  }

  // ---- Errors you can fix column ----

  private loadErrorGroups(): void {
    this.loadingErrorGroups = true;
    forkJoin({
      actionable: this.sageService
        .getActionableErrors({ page: 1, pageSize: 200 })
        .pipe(catchError(() => of(null))),
      latest: this.sageService
        .getLatestSessionErrors({ page: 1, pageSize: 1 })
        .pipe(catchError(() => of(null))),
    }).subscribe(({ actionable, latest }) => {
      this.loadingErrorGroups = false;
      if (!actionable) {
        this.toast.error('Could not load the errors you can fix.');
        return;
      }

      this.errorGroups = this.buildErrorGroups(actionable.items || [], actionable.codeCounts || []);
      // An open reason follows the reload, and says it's fixed once nothing is left of it. Units
      // judge that from their own review, which the units section reloads.
      if (this.errorFix && this.errorFix.fix !== 'units') {
        this.errorFix = this.errorGroups.find((g) => g.code === this.errorFix!.code) ?? { ...this.errorFix, fixed: true };
        this.fixStore.follow(this.errorFix);
      }
      this.actionableByEntity = actionable.entityCounts
        ? Object.fromEntries(actionable.entityCounts.map((c) => [c.entityType, c.count]))
        : this.countActionableByEntity(actionable.items || []);

      // The two endpoints scope differently, so the difference can come out negative — clamp
      // rather than show a nonsense count.
      this.retryingErrorCount = latest
        ? Math.max(0, (latest.totalCount || 0) - (actionable.totalCount || 0))
        : 0;
    });
  }

  private buildErrorGroups(
    items: SageActionableError[],
    codeCounts: SageSyncErrorCodeCount[]
  ): ErrorGroup[] {
    const recordsByCode = new Map<string, ErrorRecordRow[]>();
    for (const item of items) {
      const code = item.latest.errorCode || 'unknown';
      const rows = recordsByCode.get(code) ?? [];
      rows.push({
        id: item.latest.id,
        name: item.latest.recordDisplayName || item.latest.recordLabel,
        detail: item.latest.errorMessage,
        recordId: item.latest.recordId ?? null,
        aivorRecordId: item.latest.aivorRecordId ?? null,
        entityType: item.latest.entityType,
        errorCode: code,
      });
      recordsByCode.set(code, rows);
    }

    // codeCounts is the server's own rollup — trust it for the count rather than recounting the
    // page of items, which is capped.
    const countByCode = new Map(codeCounts.map((c) => [c.errorCode, c.count]));

    return [...recordsByCode.entries()]
      .map(([code, records]) => this.errorGroupFor(code, records, countByCode.get(code) ?? records.length))
      .sort((a, b) => b.count - a.count);
  }

  private errorGroupFor(code: string, records: ErrorRecordRow[], count: number): ErrorGroup {
    const copy = actionableErrorCopy(code);
    const fix = SAGE_FIX[code] ?? null;
    // A reason the next sync clears is labelled with its sync, when its records are of one type.
    const syncs = fix === 'sync' && copy.kind === 'waits' ? syncTargetsFor(fix, [...new Set(records.map((r) => r.entityType))]) : [];
    return {
      code,
      label: copy.label,
      // Codes logged for every record type take their pill from the records.
      entityPill: copy.entityPill || (records[0] ? entityLabelForType(records[0].entityType) : ''),
      whatToDo: copy.whatToDo,
      action: copy.action ?? (syncs.length === 1 ? syncs[0].label : null),
      fix,
      clears: SAGE_FIX_CLEARS[copy.kind],
      count,
      records,
    };
  }

  // Only for a server from before entityCounts; the page it counts is capped at 100.
  private countActionableByEntity(items: SageActionableError[]): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const item of items) {
      const type = item.latest.entityType;
      if (type) counts[type] = (counts[type] ?? 0) + 1;
    }
    return counts;
  }

  actionableCountFor(entityType: number): number {
    return this.actionableByEntity[entityTypeName(entityType)] ?? 0;
  }

  /** Every reason opens the fix panel; units also load their section. */
  fixErrorGroup(group: ErrorGroup): void {
    this.errorFix = group;
    this.fixSyncKey = null;
    this.fixSynced = false;
    this.fixStore.open(group);
    if (group.fix === 'units') this.openUnitFix();
  }

  closeErrorFix(): void {
    if (this.unitFix && (this.unitFix.applying || this.unitFix.savingIds.size)) return;
    this.errorFix = null;
    this.unitFix = null;
    this.fixSyncKey = null;
    this.fixStore.close();
  }

  /** The panel's buttons that leave it: Manufacturing, or the tab that fixes the reason. */
  runErrorFixAction(group: ErrorGroup): void {
    switch (group.fix) {
      case 'bills-of-materials':
        this.closeErrorFix();
        this.router.navigate(['/manufacturing']);
        break;
      case 'review':
      case 'matches':
        this.closeErrorFix();
        // A link problem is fixed in the link list, which Finished goods hides.
        if (this.fixSection === 'FinishedGood') this.fixSection = this.fixType;
        this.selectTab(group.fix);
        break;
    }
  }

  /** The panel stays open: when the sync ends the list reloads, and the reason clears or shows why not. */
  syncForFix(target: SyncTarget): void {
    if (this.syncState.kind !== 'idle' || !this.syncEnabled) return;
    this.fixSyncKey = target.type ?? 'all';
    this.fixSynced = false;
    if (target.type) this.syncEntity(entityTypeNumber(target.type));
    else this.syncAllNow();
  }

  get fixSyncing(): string | null {
    return this.syncState.kind === 'idle' ? null : this.fixSyncKey;
  }

  get syncBusy(): boolean {
    return this.syncState.kind !== 'idle';
  }

  /** Re-matching a record to another Aivor record happens on its matching screen (decided 26/10/2026). */
  openMatchingForFix(type: SageEntityTypeName): void {
    const slug = entitySlug(entityTypeNumber(type));
    if (!slug) return;
    this.closeErrorFix();
    this.router.navigate(['/sage-integration', slug], { queryParams: { from: 'console' } });
  }

  // ---- Stock corrections column ----

  // Unresolved only: Pending and Failed. A Failed correction still needs the user, so hiding it
  // would make the column disagree with the tab badge, which sums the same two statuses.
  private loadDashboardStock(): void {
    this.loadingDashStock = true;
    forkJoin(
      this.OPEN_STOCK_STATUSES.map((status) =>
        this.sageService
          .getStockAdjustments({ status, page: 1, pageSize: 5 })
          .pipe(catchError(() => of(null)))
      )
    ).subscribe((pages) => {
      this.loadingDashStock = false;
      if (pages.some((p) => p === null)) {
        this.toast.error('Could not load stock corrections.');
        return;
      }

      this.dashStockRows = pages
        .flatMap((p) => p!.items || [])
        .sort((a, b) => Date.parse(b.detectedAt) - Date.parse(a.detectedAt))
        .slice(0, 5);
      this.dashStockTotal = pages.reduce((sum, p) => sum + (p!.totalCount || 0), 0);
      this.stockBadgeCount = this.dashStockTotal;
    });

    if (this.stockReasons.length === 0) {
      this.sageService.getStockAdjustmentReasons().subscribe({
        next: (res) => (this.stockReasons = res.items || []),
        error: () => this.toast.error('Could not load adjustment reasons.'),
      });
    }
  }

  onDashStockChanged(): void {
    this.loadDashboardStock();
  }

  get showSyncStepper(): boolean {
    const s = this.syncState;
    return s.kind === 'all' || (s.kind === 'complete' && s.scope === 'all');
  }

  /** Sync all has no cancel endpoint to offer, so while it (or a run this page didn't start) is
   * going, its button becomes a disabled "Syncing…". */
  get syncAllShowsSyncing(): boolean {
    const s = this.syncState;
    return s.kind === 'all' || s.kind === 'external' || (s.kind === 'complete' && typeof s.scope !== 'number');
  }

  get lastSyncedAt(): string | null {
    return latestSyncedAt(this.entityStatuses);
  }

  get connSubtitle(): string {
    const s = this.syncState;
    switch (s.kind) {
      case 'all': {
        const p = this.syncProgress;
        if (p.pullPhase) return 'Checking Sage for changes · page refreshes when finished';
        if (p.currentIndex < 0) return 'Starting sync · page refreshes when finished';
        return `Syncing ${p.steps[p.currentIndex].label} · ${p.currentIndex + 1} of ${p.steps.length} · page refreshes when finished`;
      }
      case 'single':
        return `Syncing ${entityLabel(s.entityType)}. Other syncs are paused until it finishes.`;
      case 'external':
        return 'A sync is already running · page refreshes when finished';
      case 'complete':
        return s.failed ? 'Sync finished with errors — refreshing…' : 'Sync complete — refreshing…';
      default:
        return this.syncEnabled
          ? 'Suppliers, categories and finished goods sync automatically every 12 hours.'
          : 'Automatic syncing is paused for this organisation. The Sage connection stays active.';
    }
  }

  cardSyncState(entityType: number): 'syncing' | 'done' | 'failed' | null {
    const s = this.syncState;
    if (s.kind === 'single' && s.entityType === entityType) return 'syncing';
    if (this.failedEntities.has(entityType)) return 'failed';
    if (s.kind === 'complete' && s.scope === entityType) return 'done';
    return null;
  }

  syncEntity(entityType: number): void {
    if (this.syncState.kind !== 'idle') return;
    this.startSync(
      { kind: 'single', entityType, baseline: syncBaseline(this.entityStatuses) },
      entityTypeName(entityType),
      `Could not sync ${entityLabel(entityType)}. Please try again.`
    );
  }

  syncAllNow(): void {
    if (this.syncState.kind !== 'idle') return;
    this.startSync(
      { kind: 'all', baseline: syncBaseline(this.entityStatuses) },
      undefined,
      'Could not start sync. Please try again.'
    );
  }

  private startSync(
    state: Extract<SyncState, { kind: 'all' | 'single' }>,
    entityType: SageEntityTypeName | undefined,
    fallbackMessage: string
  ): void {
    this.syncState = state;
    this.syncProgress = buildSyncSteps(this.entityStatuses, state.baseline, { started: false, ended: false });

    // Re-read the baseline right before triggering: rows a scheduled sweep wrote since the page
    // loaded would otherwise count as this run's.
    this.sageService
      .getSyncStatus()
      .pipe(
        catchError(() => of(null)),
        switchMap((res) => {
          if (res) {
            this.entityStatuses = inMatchingOrder(res.entities || []);
            this.syncState = { ...state, baseline: syncBaseline(this.entityStatuses) };
          }
          return this.sageService.triggerSync(entityType);
        })
      )
      .subscribe({
        next: () => {
          if (state.kind === 'all') {
            this.failedEntities.clear();
          } else {
            this.failedEntities.delete(state.entityType);
          }
          this.beginSyncWatch();
        },
        error: (err) => {
          this.syncState = SYNC_IDLE;
          // The panel's run never started; a 409 watches someone else's.
          this.fixSyncKey = null;
          this.handleTriggerError(err, fallbackMessage);
        },
      });
  }

  // A 409 means a sync is already under way — that's not a failure, it just means someone (or the
  // scheduler) got there first, so watch that run instead of reporting an error. SyncDisabled is
  // the other 409 this endpoint returns, and it has no run to watch.
  private handleTriggerError(err: any, fallbackMessage: string): void {
    if (isSyncDisabledError(err)) {
      this.syncEnabled = false;
      this.toast.error(err.error.message || 'Sage sync is paused.');
      return;
    }
    if (err?.status === 409) {
      this.toast.success('A sync is already running.');
      this.syncState = { kind: 'external' };
      this.beginSyncWatch();
      return;
    }
    this.toast.error(sageErrorMessageFor(err) ?? fallbackMessage);
  }

  // The trigger endpoint only queues the run, so completion has to be polled for. Watching
  // isSyncing (the orchestrator's own lock) rather than log rows avoids treating a run that
  // crashed mid-sweep — and left its log stuck at Running — as though it were still going.
  private beginSyncWatch(): void {
    // A trigger or status response can land after the page has closed.
    if (this.destroyed) return;
    this.stopSyncWatch();
    this.sawSyncRunning = false;
    this.syncWatchStartedAt = Date.now();
    // Started while the app is in the background: wait until it's visible again rather than poll
    // at a throttled rate against a clock that keeps running.
    if (document.hidden) {
      this.hiddenAt = this.syncWatchStartedAt;
      return;
    }
    this.startSyncPolling();
  }

  private startSyncPolling(): void {
    this.syncPollHandle = setInterval(() => this.pollSyncStatus(), this.SYNC_POLL_MS);
  }

  // Browsers throttle or freeze timers in a background tab or installed PWA, so without this the
  // first tick after a long absence hits the timeout even when the run finished long ago.
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.hidden) {
      if (this.syncPollHandle === null) return;
      this.stopSyncWatch();
      this.hiddenAt = Date.now();
      return;
    }

    if (this.hiddenAt === null) return;
    // Only visible time counts towards the start grace and the timeout.
    this.syncWatchStartedAt += Date.now() - this.hiddenAt;
    this.hiddenAt = null;

    const kind = this.syncState.kind;
    if (kind !== 'all' && kind !== 'single' && kind !== 'external') return;
    // Not beginSyncWatch(): that would reset sawSyncRunning, so a run that finished while hidden
    // would read as never having started. Interval first so this poll can still end the watch.
    this.startSyncPolling();
    this.pollSyncStatus();
  }

  private pollSyncStatus(): void {
    if (Date.now() - this.syncWatchStartedAt > this.SYNC_WATCH_TIMEOUT_MS) {
      this.endSyncWatch();
      this.toast.error('Sync is taking longer than expected — reload to see the latest status.');
      return;
    }

    this.sageService.getSyncStatus().subscribe({
      next: (res) => {
        const live = res.entities || [];
        this.entityStatuses = inMatchingOrder(mergeLiveStatuses(this.entityStatuses, live));

        const state = this.syncState;
        const baseline = state.kind === 'all' || state.kind === 'single' ? state.baseline : null;
        // A run quicker than one poll interval is never caught with isSyncing true, but its rows
        // still give it away.
        const runRowsSeen =
          !!baseline && live.some((e) => isRowFromRun(e.outbound, baseline) || isRowFromRun(e.inbound, baseline));
        if (res.isSyncing || runRowsSeen) {
          this.sawSyncRunning = true;
        }
        if (baseline) {
          this.syncProgress = buildSyncSteps(live, baseline, {
            started: this.sawSyncRunning,
            ended: this.sawSyncRunning && !res.isSyncing,
          });
        }

        if (res.isSyncing) return;

        // Queued but not picked up yet reads as "not syncing" too, so hold the indicator until
        // the run has actually been seen starting.
        if (!this.sawSyncRunning) {
          if (Date.now() - this.syncWatchStartedAt < this.SYNC_START_GRACE_MS) return;
          // An external run most likely finished between the 409 and the first poll.
          if (state.kind !== 'external') {
            this.endSyncWatch();
            this.toast.warning("The sync was queued but hasn't started yet — check back shortly.");
            return;
          }
        }

        this.completeSync();
      },
      // Transient failures just mean the next tick retries; the timeout above is the backstop.
      error: () => {},
    });
  }

  private completeSync(): void {
    if (this.destroyed) return;
    this.stopSyncWatch();
    const s = this.syncState;
    if (s.kind === 'all' || s.kind === 'single') {
      for (const step of this.syncProgress.steps) {
        if (step.status === 'failed') this.failedEntities.add(step.entityType);
      }
    }
    const failed = (s.kind === 'all' || s.kind === 'single') && this.syncProgress.anyFailed;
    this.syncState = {
      kind: 'complete',
      scope: s.kind === 'single' ? s.entityType : s.kind === 'all' ? 'all' : 'external',
      failed,
    };
    // Set only while the panel's own run is in flight. A failed run keeps its sync buttons for a retry.
    if (this.fixSyncKey) {
      this.fixSynced = !failed;
      this.fixSyncKey = null;
    }

    this.syncCompleteHandle = setTimeout(() => {
      this.syncCompleteHandle = null;
      this.syncState = SYNC_IDLE;
      this.loadSyncDashboard();
      this.loadTabBadgeCounts();
      if (this.activeTab !== 'sync') {
        this.loadTab(this.activeTab);
      }
    }, this.SYNC_COMPLETE_HOLD_MS);
  }

  private endSyncWatch(): void {
    this.stopSyncWatch();
    this.syncState = SYNC_IDLE;
    this.fixSyncKey = null;
  }

  private stopSyncWatch(): void {
    if (this.syncPollHandle !== null) {
      clearInterval(this.syncPollHandle);
      this.syncPollHandle = null;
    }
  }

  // ---- Review queue ----

  private loadReview(): void {
    this.sageService.getPendingReview().subscribe({
      next: (res) => {
        this.reviewEntities = res.entities || [];
        this.reviewTotalCount = res.totalCount;
        this.applyReviewCount(res.totalCount);
      },
      error: () => this.toast.error('Could not load records awaiting review.'),
    });
  }

  // "Apply all" confirms in this order, and a bundle applied before its items are linked is blocked.
  get reviewEntitiesOrdered(): SagePendingReviewEntity[] {
    return [...this.reviewEntities].sort(
      (a, b) =>
        entityApplyOrder(a.entityType) - entityApplyOrder(b.entityType) ||
        Date.parse(a.sessionCreatedAt) - Date.parse(b.sessionCreatedAt)
    );
  }

  // Rows are one per session, and a single entity type can hold more than one InProgress session —
  // counting rows would overstate how many entities are actually waiting.
  get reviewEntityCount(): number {
    return new Set(this.reviewEntities.map((r) => r.entityType)).size;
  }

  get oldestReviewAge(): string {
    const oldest = this.reviewEntitiesOrdered[0];
    return oldest ? ageLabel(oldest.sessionCreatedAt) : '';
  }

  get reviewEntityNames(): string {
    const names = [...new Set(this.reviewEntitiesOrdered.map((r) => entityLabel(r.entityType)))];
    if (names.length <= 1) return names[0] ?? 'these records';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }

  get emptyEntityLabels(): string[] {
    const queued = new Set(this.reviewEntities.map((r) => entityTypeName(r.entityType)));
    return getEnabledEntityRoutes()
      .filter((e) => !queued.has(e.type))
      .map((e) => e.label);
  }

  // Advisory only, derived purely from what is queued: a category has to be mapped before the
  // records that reference it, the same ordering the onboarding screen spells out in its footer.
  private readonly REVIEW_DEPENDANTS: Partial<Record<SageEntityTypeName, SageEntityTypeName>> = {
    SupplierCategory: 'Supplier',
    ItemCategory: 'Item',
  };

  dependencyPill(row: SagePendingReviewEntity): string | null {
    const dependant = this.REVIEW_DEPENDANTS[entityTypeName(row.entityType)];
    if (!dependant) return null;
    if (!this.reviewEntities.some((r) => entityTypeName(r.entityType) === dependant)) return null;

    const label = ENTITY_ROUTE_ORDER.find((e) => e.type === dependant)?.label;
    return label ? `Match before ${label}` : null;
  }

  openReview(row: SagePendingReviewEntity): void {
    const slug = entitySlug(row.entityType);
    if (!slug) return;
    this.router.navigate(['/sage-integration', slug], { queryParams: { from: 'console' } });
  }

  get applyBusy(): boolean {
    return this.applyingAllReview || this.applyingReviewSessionId !== null;
  }

  openApplyAllConfirm(): void {
    if (this.applyBusy || !this.syncEnabled || !this.reviewEntities.length) return;
    this.showApplyAllConfirm = true;
  }

  cancelApplyAll(): void {
    this.showApplyAllConfirm = false;
  }

  // Sequential, not forkJoin: each confirm fans out to an outbound Sage push, and Sage allows only
  // 100 requests/minute per company — a parallel burst risks a 429 and the hour-long IP block.
  applyAllReviewed(): void {
    if (this.applyBusy || !this.syncEnabled) return;

    const rows = this.reviewEntitiesOrdered;
    if (!rows.length) return;

    this.showApplyAllConfirm = false;
    this.applyingAllReview = true;

    // A Set, not a list: an entity type with two open sessions would otherwise be named twice.
    const applied = new Set<string>();
    const appliedNames = () => [...applied].join(', ');
    let carried = 0;
    const appliedMessage = () =>
      carried > 0
        ? `${appliedNames()} applied — ${carried} record${carried === 1 ? '' : 's'} still need attention.`
        : `${appliedNames()} applied.`;

    concat(
      ...rows.map((row) =>
        this.sageService.confirmSession(row.sessionId).pipe(map((result) => ({ row, result })))
      )
    ).subscribe({
      next: ({ row, result }) => {
        applied.add(entityLabel(row.entityType));
        carried += result.carriedForwardCount ?? 0;
      },
      error: (err) => {
        this.applyingAllReview = false;
        this.loadReview();

        if (applied.size) {
          this.toast.success(appliedMessage());
        }
        if (isSyncDisabledError(err)) {
          this.syncEnabled = false;
          this.toast.error(err.error.message || 'Sage sync is paused.');
          return;
        }
        this.toast.error(sageErrorMessageFor(err) ?? 'Could not apply every record type. Anything left is still in the queue.');
      },
      complete: () => {
        this.applyingAllReview = false;
        this.toast.success(appliedMessage());
        this.loadReview();
        // Applying fans out to outbound pushes, so the other tabs' counts can have moved too.
        this.loadTabBadgeCounts();
      },
    });
  }

  applyReview(row: SagePendingReviewEntity): void {
    if (this.applyBusy || !this.syncEnabled) return;
    this.applyingReviewSessionId = row.sessionId;

    this.sageService.confirmSession(row.sessionId).subscribe({
      next: (result) => {
        this.applyingReviewSessionId = null;
        const carried = result.carriedForwardCount ?? 0;
        // Without naming them, the entity reappearing in the review list reads as a failed apply.
        this.toast.success(
          carried > 0
            ? `${entityLabel(row.entityType)} applied — ${carried} record${carried === 1 ? '' : 's'} still need attention.`
            : `${entityLabel(row.entityType)} applied.`
        );
        this.loadReview();
        this.loadTabBadgeCounts();
      },
      error: (err) => {
        this.applyingReviewSessionId = null;
        if (isSyncDisabledError(err)) {
          this.syncEnabled = false;
          this.toast.error(err.error.message || 'Sage sync is paused.');
          return;
        }
        this.toast.error(sageErrorMessageFor(err) ?? `Could not apply ${entityLabel(row.entityType)}. Please try again.`);
      },
    });
  }

  // ---- Issue lists (Errors and Conflicts tabs) ----
  //
  // Both map onto SageIssueRow, but they are two unrelated endpoints, so each keeps its own rows,
  // search, page and counts. Only Errors uses the expandable row template; Conflicts is a flat
  // one-row-per-field table.

  // Tenant-wide and unfiltered, so a badge is right before its tab has ever been opened. pageSize
  // 1 because only the counts are wanted. Conflicts is the only issue list still carrying a tab
  // badge; Errors and Review queue are reached from the dashboard columns instead.
  private loadTabBadgeCounts(): void {
    this.sageService.getConflicts({ page: 1, pageSize: 1 }).subscribe({
      next: (res) => (this.conflictBadgeCount = res.totalCount),
      error: () => {},
    });
    // Still fetched with no tab to show it: the sidebar count comes from this push. The Review
    // panel's own load hits the same endpoint, and so does the dashboard's review column — don't
    // fetch it twice when either is open.
    if (this.activeTab !== 'review' && this.activeTab !== 'sync') {
      this.loadReviewBadgeCount();
    }
    // Likewise the Sync dashboard already sums the same two status probes for its stock column.
    if (this.activeTab !== 'sync') {
      this.loadStockBadgeCount();
    }
  }

  private loadReviewBadgeCount(): void {
    this.sageService.getPendingReview().subscribe({
      next: (res) => this.applyReviewCount(res.totalCount),
      error: () => {},
    });
  }

  // The overview used to be the only producer, so without the service push the sidebar count would
  // sit at 0 for exactly the onboarded tenants this console serves.
  private applyReviewCount(total: number): void {
    this.sageService.setReviewCount(total);
  }

  // Mirrors isStockAdjustmentOpen, which names the same two statuses by number. The endpoint
  // filters one status at a time, so the badge is the sum of two count-only probes.
  private readonly OPEN_STOCK_STATUSES = ['Pending', 'Failed'];

  private loadStockBadgeCount(): void {
    forkJoin(
      this.OPEN_STOCK_STATUSES.map((status) =>
        this.sageService.getStockAdjustments({ status, page: 1, pageSize: 1 }).pipe(
          map((res) => res.totalCount as number | null),
          catchError(() => of(null))
        )
      )
    ).subscribe((counts) => {
      // A half-failed sum would understate the badge — keep the last good value instead.
      if (counts.some((c) => c === null)) return;
      this.stockBadgeCount = counts.reduce<number>((sum, c) => sum + (c ?? 0), 0);
    });
  }

  // ---- Errors ----

  private loadErrors(): void {
    // A slower earlier reply would otherwise land last and show results for an older search.
    this.errorsRequest?.unsubscribe();
    this.loadingErrors = true;
    this.loadedErrorSearch = this.errorSearch;
    this.errorsRequest = this.sageService
      .getSyncErrors({
        page: this.errorPage,
        pageSize: this.pageSize,
        category: this.selectedErrorCategories.length ? this.selectedErrorCategories : undefined,
        search: this.errorSearch.trim() || undefined,
        includeResolved: this.showResolvedErrors || undefined,
      })
      .subscribe({
        next: (res) => {
          this.loadingErrors = false;
          this.errorRows = (res.items || []).map(issueFromError);
          this.errorCategoryCounts = res.categoryCounts || [];
          this.errorFilteredCount = res.filteredCount;
          this.errorTotalCount = res.totalCount;
        },
        error: () => {
          this.loadingErrors = false;
          this.toast.error('Could not load sync errors.');
        },
      });
  }

  get errorTotalPages(): number {
    return Math.max(1, Math.ceil(this.errorFilteredCount / this.pageSize));
  }

  get reasonChipsVisible(): boolean {
    return this.errorCategoryCounts.length > 0;
  }

  onErrorSearchChange(value: string): void {
    this.errorSearch = value;
    this.errorSearch$.next(value);
  }

  toggleErrorCategory(category: string): void {
    if (this.loadingErrors) return;
    const idx = this.selectedErrorCategories.indexOf(category);
    if (idx === -1) {
      this.selectedErrorCategories.push(category);
    } else {
      this.selectedErrorCategories.splice(idx, 1);
    }
    this.errorPage = 1;
    this.collapseIssueRow();
    this.loadErrors();
  }

  toggleResolvedErrors(): void {
    if (this.loadingErrors) return;
    this.showResolvedErrors = !this.showResolvedErrors;
    // The two views have different reason chips, and a selected reason with no chip can't be cleared.
    this.selectedErrorCategories = [];
    this.errorPage = 1;
    this.collapseIssueRow();
    this.loadErrors();
  }

  goToErrorPage(page: number): void {
    if (page < 1 || page > this.errorTotalPages || page === this.errorPage || this.loadingErrors) return;
    this.errorPage = page;
    this.collapseIssueRow();
    this.loadErrors();
  }

  // ---- Conflicts ----

  private loadConflicts(): void {
    this.loadingConflicts = true;
    const filter: ConflictLogFilter = {
      page: this.conflictPage,
      pageSize: this.pageSize,
      search: this.conflictSearch.trim() || undefined,
    };
    if (this.selectedConflictEntityType) {
      filter.entityType = this.selectedConflictEntityType;
    }

    this.sageService.getConflicts(filter).subscribe({
      next: (res) => {
        this.loadingConflicts = false;
        this.conflictRows = (res.items || []).map(issueFromConflict);
        this.conflictFilteredCount = res.totalCount;
        // Unlike the errors endpoint, this totalCount narrows with both filters.
        if (!filter.search && !filter.entityType) {
          this.conflictBadgeCount = res.totalCount;
        }
      },
      error: () => {
        this.loadingConflicts = false;
        this.toast.error('Could not load conflicts.');
      },
    });
  }

  get conflictTotalPages(): number {
    return Math.max(1, Math.ceil(this.conflictFilteredCount / this.pageSize));
  }

  onConflictSearchChange(value: string): void {
    this.conflictSearch = value;
    this.conflictSearch$.next(value);
  }

  onConflictEntityTypeChange(): void {
    this.conflictPage = 1;
    this.loadConflicts();
  }

  goToConflictPage(page: number): void {
    if (page < 1 || page > this.conflictTotalPages || page === this.conflictPage || this.loadingConflicts) return;
    this.conflictPage = page;
    this.loadConflicts();
  }

  // ---- Error row interaction ----

  private collapseIssueRow(): void {
    this.expandedIssueKey = null;
    this.expandedErrorDetail = null;
  }

  toggleIssueRow(row: SageIssueRow): void {
    if (this.expandedIssueKey === row.key) {
      this.collapseIssueRow();
      return;
    }
    this.collapseIssueRow();
    this.expandedIssueKey = row.key;

    // Only fetched when there is a raw Sage response body to show.
    if (row.type !== 'error' || !row.error?.hasResponseBody) return;
    this.loadingErrorDetail = true;
    this.sageService.getSyncErrorById(row.id).subscribe({
      next: (detail) => {
        this.loadingErrorDetail = false;
        this.expandedErrorDetail = detail;
      },
      error: () => {
        this.loadingErrorDetail = false;
      },
    });
  }

  readonly entityLabelFor = entityLabelForType;
  readonly entityIconFor = entityIconForType;
  readonly entityAccentFor = entityAccentForType;
  readonly entityAccentSoftFor = entityAccentSoftForType;

  // ---- Stock corrections ----

  private loadStockAdjustments(): void {
    this.sageService.getStockAdjustments({ page: 1, pageSize: 25 }).subscribe({
      next: (res) => (this.stockAdjustments = res.items || []),
      error: () => this.toast.error('Could not load stock corrections.'),
    });
    this.loadStockBadgeCount();
    if (this.stockReasons.length === 0) {
      this.sageService.getStockAdjustmentReasons().subscribe({
        next: (res) => (this.stockReasons = res.items || []),
        error: () => this.toast.error('Could not load adjustment reasons.'),
      });
    }
  }

  isStockAdjustmentOpen(row: SageItemAdjustment): boolean {
    return row.status === 1 || row.status === 4;
  }

  openReleaseDialog(row: SageItemAdjustment): void {
    this.releasingAdjustment = row;
    this.selectedReasonId = row.reasonId ?? null;
  }

  cancelRelease(): void {
    this.releasingAdjustment = null;
    this.selectedReasonId = null;
  }

  confirmRelease(): void {
    if (!this.releasingAdjustment || !this.selectedReasonId || this.releasing) return;
    this.releasing = true;
    this.sageService.releaseStockAdjustment(this.releasingAdjustment.id, this.selectedReasonId).subscribe({
      next: (res) => {
        this.releasing = false;
        this.releasingAdjustment = null;
        this.selectedReasonId = null;
        this.toast.success(
          res.status === 'AlreadyInSync'
            ? 'Quantities already matched — no correction was needed.'
            : 'Stock correction applied.'
        );
        this.loadStockAdjustments();
      },
      error: (err) => {
        this.releasing = false;
        this.toast.error(err?.error?.message || 'Could not apply the stock correction.');
      },
    });
  }

  cancelStockAdjustment(row: SageItemAdjustment): void {
    this.sageService.cancelStockAdjustment(row.id).subscribe({
      next: () => {
        this.toast.success('Stock correction dismissed.');
        this.loadStockAdjustments();
      },
      error: () => this.toast.error('Could not dismiss the stock correction.'),
    });
  }

  // ---- Fix matches ----

  loadFixMatches(): void {
    const type = this.fixType;
    this.loadingLinks = true;
    this.linksLoadError = false;

    forkJoin({
      rows: this.sageService.getLinks(type),
      // Only feeds a pill and a filter, so the list still shows without it.
      issues: this.sageService.getActionableErrors({ pageSize: 100 }).pipe(
        map((page) => page.items ?? []),
        catchError(() => of([] as SageActionableError[]))
      ),
    }).subscribe({
      next: ({ rows, issues }) => {
        if (type !== this.fixType) return;
        this.linkRows = rows ?? [];
        this.linkAttention = needsAttentionByRecord(issues, type);
        this.loadingLinks = false;
        this.linkCounts = { ...this.linkCounts, [type]: linkedRows(this.linkRows).length };
        const other: FixMatchType = type === 'Item' ? 'Supplier' : 'Item';
        if (this.linkCounts[other] == null) this.loadLinkCount(other);
      },
      error: () => {
        if (type !== this.fixType) return;
        this.loadingLinks = false;
        this.linksLoadError = true;
      },
    });

    if (type === 'Item' && !this.linkUnits.length) this.loadLinkUnits();
  }

  private loadLinkCount(type: FixMatchType): void {
    this.sageService.getLinks(type).subscribe({
      next: (rows) => (this.linkCounts = { ...this.linkCounts, [type]: linkedRows(rows ?? []).length }),
      error: () => {},
    });
  }

  private loadLinkUnits(): void {
    this.sageService.getUnitsOfMeasure().subscribe({
      next: (units) => (this.linkUnits = (units ?? []).map(toUnitOfMeasureOption)),
      error: () => this.toast.error('Could not load the units of measure.'),
    });
  }

  private loadFixSection(): void {
    if (this.fixSection !== 'FinishedGood') {
      this.loadFixMatches();
      return;
    }
    this.loadTargets();
    for (const type of ['Item', 'Supplier'] as FixMatchType[]) {
      if (this.linkCounts[type] == null) this.loadLinkCount(type);
    }
  }

  setFixSection(section: FixMatchesSection): void {
    if (section === this.fixSection) return;
    this.fixSection = section;
    if (section !== 'FinishedGood' && section !== this.fixType) {
      this.fixType = section;
      this.linksSearch = '';
      this.linksChip = 'all';
      this.linkRows = [];
      this.linkAttention = new Map();
    }
    this.loadFixSection();
  }

  get fixSectionCounts(): Record<FixMatchesSection, number | null> {
    return { ...this.linkCounts, FinishedGood: this.finishedGoodCount };
  }

  get fixNoun(): string {
    return fixMatchNoun(this.fixType);
  }

  get fixChips() {
    return fixChips(this.fixType);
  }

  get filteredLinkRows(): SageLinkRow[] {
    return filterLinkRows(this.linkRows, this.linksSearch, this.linksChip, this.linkAttention);
  }

  get linkedCount(): number {
    return linkedRows(this.linkRows).length;
  }

  get emptyLinksText(): string {
    return emptyLinksText(this.fixType, this.linksSearch, this.linksChip);
  }

  fixChipLabel(chip: { key: FixMatchChip; label: string }): string {
    return fixChipLabel(chip, this.linkChipCount('attention'));
  }

  linkChipCount(chip: FixMatchChip): number {
    return chipCount(this.linkRows, chip, this.linkAttention);
  }

  linkAttentionFor(row: SageLinkRow): string | null {
    return this.linkAttention.get(row.aivorRecordId) ?? null;
  }

  isLinkFixed(row: SageLinkRow): boolean {
    return this.fixedLinks[this.fixType].has(row.aivorRecordId);
  }

  get fixMatchesView(): FixMatchesView {
    return {
      section: this.fixSection,
      type: this.fixType,
      rows: this.filteredLinkRows,
      allRows: this.linkRows,
      loading: this.loadingLinks,
      loadError: this.linksLoadError,
      search: this.linksSearch,
      chip: this.linksChip,
      chipCounts: {
        all: this.linkChipCount('all'),
        attention: this.linkChipCount('attention'),
        raw: this.linkChipCount('raw'),
        finished: this.linkChipCount('finished'),
      },
      sectionCounts: this.fixSectionCounts,
      attention: this.linkAttention,
      fixed: this.fixedLinks[this.fixType],
      changing: this.changingLink,
      units: this.linkUnits,
      saving: this.savingLink,
      error: this.linkError,
    };
  }

  openLinkChange(row: SageLinkRow): void {
    this.changingLink = row;
    this.linkError = null;
  }

  closeLinkChange(): void {
    if (this.savingLink) return;
    this.changingLink = null;
    this.linkError = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.errorFix) this.closeErrorFix();
    else if (this.changingLink) this.closeLinkChange();
  }

  saveLinkMove(request: SageLinkMoveRequest): void {
    const type = this.fixType;
    this.savingLink = true;
    this.linkError = null;
    this.sageService.moveLink(type, request).subscribe({
      next: (details) => this.linkSaved(type, details.linkedAivorRecordId, moveToast(details)),
      error: (err) => this.linkSaveFailed(err),
    });
  }

  saveLinkAdd(request: SageLinkAddRequest): void {
    const type = this.fixType;
    this.savingLink = true;
    this.linkError = null;
    this.sageService.addLinkAsNew(type, request).subscribe({
      next: (details) => this.linkSaved(type, details.newAivorRecordId, ADD_AS_NEW_TOAST),
      error: (err) => this.linkSaveFailed(err),
    });
  }

  private linkSaved(type: FixMatchType, aivorRecordId: number, message: string): void {
    this.savingLink = false;
    this.changingLink = null;
    this.fixedLinks[type].add(aivorRecordId);
    this.toast.success(message);
    this.loadFixMatches();
  }

  private linkSaveFailed(err: any): void {
    this.savingLink = false;
    if (isSyncDisabledError(err)) this.syncEnabled = false;
    this.linkError =
      (err?.status === 409 && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't save. Try again.";
  }

  // ---- Units for Sage items that didn't come in ----

  openUnitFix(): void {
    this.unitFixSavingIds.clear();
    this.unitsApplied = false;
    this.unitFix = { ...unitFixView([], this.unitFixUnits, new Map()), loading: true };
    this.loadUnitFix(new Map());
  }

  /** `reasons` are the last Apply's refusals by row. A refused row that left the review has no card,
   * so its reason goes in the panel's own message. */
  private loadUnitFix(reasons: ReadonlyMap<number, string>): void {
    forkJoin({
      // No open Items review is a 404: nothing is waiting.
      review: this.sageService.getSession('Item').pipe(catchError((err) => (err?.status === 404 ? of(null) : throwError(() => err)))),
      units: this.sageService.getUnitsOfMeasure(),
    }).subscribe({
      next: ({ review, units }) => {
        if (!this.unitFix) return;
        this.unitFixUnits = (units ?? []).map(toUnitOfMeasureOption);
        this.unitFixSessionId = review?.session.id ?? null;
        this.unitFixMatches = review?.matches ?? [];
        this.unitFixReasons = reasons;
        this.refreshUnitFix();
        const listed = new Set(this.unitFix.groups.flatMap((g) => g.items.map((i) => i.matchId)));
        const unlisted = [...reasons].filter(([matchId]) => !listed.has(matchId)).map(([, reason]) => reason);
        this.unitFix = { ...this.unitFix, error: unlisted.join(' ') || null };
        if (this.unitsApplied && !this.unitFix.total && !unlisted.length && this.errorFix) {
          this.errorFix = { ...this.errorFix, fixed: true };
        }
      },
      error: () => {
        if (this.unitFix) this.unitFix = { ...this.unitFix, loading: false, loadError: true, applying: false };
      },
    });
  }

  private refreshUnitFix(error: string | null = null): void {
    this.unitFix = {
      ...unitFixView(this.unitFixMatches, this.unitFixUnits, this.unitFixReasons, new Set(this.unitFixSavingIds)),
      error,
    };
  }

  /** Saved straight away for every item with no unit yet, or still on the group's old one; items
   * given their own unit keep it. */
  chooseFixGroupUnit(choice: { group: UnitGroupView; uomId: number }): void {
    const ids = new Set(choice.group.items.map((i) => i.matchId));
    const members = this.unitFixMatches.filter((m) => ids.has(m.id));
    const targets = groupTargets(members, groupUnit(members)).filter((m) => m.resolvedUOMID !== choice.uomId);
    this.saveFixUnits(targets, choice.uomId);
  }

  chooseFixUnit(choice: { matchId: number; uomId: number }): void {
    this.saveFixUnits(this.unitFixMatches.filter((m) => m.id === choice.matchId), choice.uomId);
  }

  private saveFixUnits(targets: SageReconciliationMatch[], uomId: number): void {
    const sessionId = this.unitFixSessionId;
    if (!this.unitFix || !sessionId || !targets.length || targets.some((m) => this.unitFixSavingIds.has(m.id))) return;
    const ids = targets.map((m) => m.id);
    ids.forEach((id) => this.unitFixSavingIds.add(id));
    this.refreshUnitFix();
    const save$ =
      ids.length === 1
        ? this.sageService.resolveUom(sessionId, ids[0], { purchaseUOMID: uomId })
        : this.sageService.resolveUoms(sessionId, ids, uomId);
    save$.subscribe({
      next: () => {
        ids.forEach((id) => this.unitFixSavingIds.delete(id));
        targets.forEach((m) => markUnitSaved(m, uomId));
        if (this.unitFix) this.refreshUnitFix();
      },
      error: (err) => {
        ids.forEach((id) => this.unitFixSavingIds.delete(id));
        if (isSyncDisabledError(err)) this.syncEnabled = false;
        if (this.unitFix) this.refreshUnitFix("Couldn't save the unit. Try again.");
      },
    });
  }

  /** The units are already saved, so Apply only adds the items. */
  applyUnitFix(): void {
    const view = this.unitFix;
    const sessionId = this.unitFixSessionId;
    const ids = unitFixApplyIds(this.unitFixMatches, this.unitFixUnits);
    if (!view || view.applying || view.savingIds.size || !sessionId || !ids.length) return;
    this.unitFix = { ...view, applying: true, error: null };
    this.sageService.confirmSelectedMatches(sessionId, ids).subscribe({
      next: (result) => this.unitFixApplied(result),
      error: (err) => this.unitFixFailed(err),
    });
  }

  // Items still waiting for a unit stay listed; once none are left the panel says it's fixed.
  private unitFixApplied(result: SageConfirmSelectedResult): void {
    if (result.applied > 0) this.toast.success(appliedToast(result.applied));
    this.loadErrorGroups();
    this.loadReviewGroups();
    this.unitsApplied = true;
    this.loadUnitFix(new Map(result.blocked.map((b) => [b.matchId, b.message])));
  }

  private unitFixFailed(err: any): void {
    if (!this.unitFix) return;
    if (isSyncDisabledError(err)) this.syncEnabled = false;
    this.unitFix = {
      ...this.unitFix,
      applying: false,
      error: (err?.status === 409 && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't apply. Try again.",
    };
  }

  // ---- Item sync targets ----

  private loadTargets(): void {
    this.sageService.getFinishedGoodsSyncTargets().subscribe({
      next: (items) => {
        this.targets = items.map((i) => {
          const saved = finishedGoodSyncTarget(i);
          const target = saved ?? 'Standalone';
          return {
            itemId: i.itemID ?? i.ItemID,
            itemName: i.itemName ?? i.ItemName ?? i.description ?? i.Description ?? '',
            itemCode: i.itemCode ?? i.ItemCode ?? '',
            categoryName: i.category?.categoryName ?? i.Category?.CategoryName ?? '',
            componentCount: i.bundleInfo?.componentCount ?? 0,
            blockedComponentCount: i.bundleInfo?.blockedComponentCount ?? 0,
            target,
            originalTarget: target,
            hasSavedTarget: saved !== null,
            sageItemId: i.sageItemId ?? i.SageItemId ?? null,
            sageBundleId: i.sageBundleId ?? i.SageBundleId ?? null,
          };
        });
        this.finishedGoodCount = this.targets.length;
      },
      error: () => this.toast.error('Could not load finished goods.'),
    });
  }

  // A row is blocked from actually syncing (regardless of its target toggle) when one or more of
  // its active-BOM components hasn't been pushed to Sage yet (SAGE.BDL.007) — the outbound sync
  // silently skips it until that's resolved, so surface the reason here instead of leaving the
  // admin to find out from the sync log.
  isTargetBlocked(row: TargetRow): boolean {
    return row.blockedComponentCount > 0;
  }

  targetBlockedReason(row: TargetRow): string {
    if (row.blockedComponentCount === 0) return '';
    const plural = row.blockedComponentCount === 1 ? "item it's made from isn't" : "items it's made from aren't";
    return `Can't sync yet: ${row.blockedComponentCount} ${plural} in Sage yet. Sync your items first.`;
  }

  get filteredTargets(): TargetRow[] {
    const q = this.targetsSearch.trim().toLowerCase();
    if (!q) return this.targets;
    return this.targets.filter(
      (t) => t.itemName.toLowerCase().includes(q) || t.itemCode.toLowerCase().includes(q)
    );
  }

  toggleTarget(row: TargetRow): void {
    row.target = row.target === 'Bundle' ? 'Standalone' : 'Bundle';
  }

  toggleProvenance(row: TargetRow): void {
    if (this.openProvenanceItemId === row.itemId) {
      this.openProvenanceItemId = null;
      return;
    }
    this.openProvenanceItemId = row.itemId;
    if (this.provenanceCache[row.itemId] && this.provenanceCache[row.itemId] !== 'error') return;
    this.provenanceCache[row.itemId] = 'loading';
    this.sageService.getProvenance('Item', row.itemId).subscribe({
      next: (p) => (this.provenanceCache[row.itemId] = p),
      error: () => (this.provenanceCache[row.itemId] = 'error'),
    });
  }

  provenanceFor(row: TargetRow): SageSyncProvenance | 'loading' | 'error' | undefined {
    return this.provenanceCache[row.itemId];
  }

  provenanceStateFor(row: TargetRow): ProvenanceSyncState | null {
    const p = this.provenanceCache[row.itemId];
    if (!p || p === 'loading' || p === 'error') return null;
    return provenanceSyncState(p);
  }

  get dirtyTargetCount(): number {
    return this.targets.filter((t) => !t.hasSavedTarget || t.target !== t.originalTarget).length;
  }

  get unsetTargetCount(): number {
    return this.targets.filter((t) => !t.hasSavedTarget).length;
  }

  saveTargets(): void {
    const dirty = this.targets.filter((t) => !t.hasSavedTarget || t.target !== t.originalTarget);
    if (dirty.length === 0 || this.savingTargets) return;
    const switches = dirty.filter(
      (t) => t.hasSavedTarget && leavesOldSageRecord(t.originalTarget, t.target, t.sageItemId, t.sageBundleId)
    );
    if (switches.length > 0) {
      this.pendingTargetSwitches = switches;
      return;
    }
    this.persistTargets(dirty);
  }

  confirmTargetSwitches(): void {
    if (!this.pendingTargetSwitches) return;
    this.pendingTargetSwitches = null;
    this.persistTargets(this.targets.filter((t) => !t.hasSavedTarget || t.target !== t.originalTarget));
  }

  cancelTargetSwitches(): void {
    this.pendingTargetSwitches = null;
  }

  private persistTargets(dirty: TargetRow[]): void {
    if (dirty.length === 0 || this.savingTargets) return;
    this.savingTargets = true;
    forkJoin(
      dirty.map((t) =>
        this.sageService.updateSyncTarget(t.itemId, t.target).pipe(
          map(() => ({ ok: true, row: t })),
          catchError(() => of({ ok: false, row: t }))
        )
      )
    ).subscribe((results) => {
      this.savingTargets = false;
      const failed = results.filter((r) => !r.ok);
      results.filter((r) => r.ok).forEach((r) => {
        r.row.originalTarget = r.row.target;
        r.row.hasSavedTarget = true;
      });
      if (failed.length > 0) {
        this.toast.error(`Could not save ${failed.length} change(s). Please try again.`);
      } else {
        this.toast.success('Changes saved.');
      }
    });
  }

  // ---- GL reporting groups ----

  reportGroups: ItemReportGroup[] = [];
  groupTypeFilter: ItemReportingGroupTypeId | 0 = 0;
  groupSearch = '';
  showInactiveGroups = false;
  loadingGroups = false;
  refreshingGroups = false;
  groupsLastSyncedAt: string | null = null;

  // Held by the fix store, whose panel sets it and offers it per item.
  get defaultSalesGroupId(): number | null {
    return this.fixStore.defaultGroupId;
  }

  set defaultSalesGroupId(id: number | null) {
    this.fixStore.defaultGroupId = id;
  }

  readonly reportGroupTypes = REPORT_GROUP_TYPES;
  readonly reportGroupType = reportGroupType;
  readonly trackGroup = (_: number, g: ItemReportGroup) => g.id;

  private loadReportGroups(): void {
    this.loadingGroups = true;
    this.sageService.getItemReportGroups().subscribe({
      next: (groups) => {
        this.loadingGroups = false;
        this.reportGroups = groups ?? [];
      },
      error: () => {
        this.loadingGroups = false;
        this.toast.error('Could not load sales and purchase groups.');
      },
    });

    // Sync timestamp + tenant default come from settings, not the groups endpoint.
    this.sageService.getTenantSageSettings().subscribe({
      next: (s) => {
        this.groupsLastSyncedAt = s.groupsLastSyncedAt ?? null;
        this.defaultSalesGroupId = s.defaultSalesReportingGroupID ?? null;
      },
      error: () => {},
    });
  }

  // Fires once on console load regardless of which tab is active, so a missing default surfaces
  // immediately instead of waiting for the user to open the Groups tab.
  private checkDefaultSalesReportingGroup(): void {
    this.sageService.getTenantSageSettings().subscribe({
      next: (s) => {
        this.groupsLastSyncedAt = s.groupsLastSyncedAt ?? null;
        this.defaultSalesGroupId = s.defaultSalesReportingGroupID ?? null;
        if (this.defaultSalesGroupId === null) {
          this.sageService.getItemReportGroups().subscribe({
            next: (groups) => {
              this.reportGroups = groups ?? [];
              this.defaultGroupNeeded = true;
              this.maybePromptDefaultGroup();
            },
            error: () => {},
          });
        }
      },
      error: () => {},
    });
  }

  // Waits for the first-visit tour, but never on another tab: the tour waits there too, so the
  // question would be hidden until the user found the dashboard.
  private maybePromptDefaultGroup(): void {
    if (!this.defaultGroupNeeded || this.tourOpen) return;
    const tourDue = !this.tourShownThisVisit && !!this.tourKey && readTourStatus(this.tourKey) === null;
    if (tourDue && this.activeTab === 'sync' && !this.entityStatusesLoaded) return;
    this.defaultGroupNeeded = false;
    this.promptSetDefaultGroup();
  }

  refreshReportGroups(): void {
    if (this.refreshingGroups || !this.syncEnabled) return;
    this.refreshingGroups = true;
    this.sageService.refreshItemReportGroups().subscribe({
      next: (res) => {
        this.refreshingGroups = false;
        this.toast.success(`${res.count} group(s) refreshed from Sage.`);
        this.loadReportGroups();
        // A group now known to Sage clears its items from Errors you can fix.
        this.loadErrorGroups();
      },
      error: (err) => {
        this.refreshingGroups = false;
        if (isSyncDisabledError(err)) this.syncEnabled = false;
        this.toast.error(sageErrorMessageFor(err) ?? 'Could not refresh groups from Sage.');
      },
    });
  }

  // Search + inactive toggle only — the type chips filter on top of this, so their
  // counts stay honest while one type is selected.
  private get groupPool(): ItemReportGroup[] {
    const q = this.groupSearch.trim().toLowerCase();
    return this.reportGroups.filter(
      (g) =>
        (this.showInactiveGroups || g.isActive) &&
        (!q ||
          g.description.toLowerCase().includes(q) ||
          String(g.sageItemReportingGroupId ?? '').includes(q))
    );
  }

  get filteredGroups(): ItemReportGroup[] {
    const pool = this.groupPool;
    return this.groupTypeFilter ? pool.filter((g) => g.itemReportingGroupTypeId === this.groupTypeFilter) : pool;
  }

  groupTypeCount(typeId: ItemReportingGroupTypeId | 0): number {
    const pool = this.groupPool;
    return typeId ? pool.filter((g) => g.itemReportingGroupTypeId === typeId).length : pool.length;
  }

  get activeGroupCount(): number {
    return this.reportGroups.filter((g) => g.isActive).length;
  }

  get inactiveGroupCount(): number {
    return this.reportGroups.length - this.activeGroupCount;
  }

  selectGroupType(typeId: ItemReportingGroupTypeId | 0): void {
    this.groupTypeFilter = typeId;
  }

  toggleInactiveGroups(): void {
    this.showInactiveGroups = !this.showInactiveGroups;
  }

  isDefaultSalesGroup(g: ItemReportGroup): boolean {
    return this.defaultSalesGroupId !== null && g.id === this.defaultSalesGroupId;
  }

  // ---- Default sales reporting group prompt ----

  showSetDefaultGroupPrompt = false;
  selectedDefaultGroupId: number | null = null;
  savingDefaultGroup = false;

  // The server refuses a default Sage doesn't know, which would block every item without its own group.
  get salesReportGroupOptions(): ItemReportGroup[] {
    return this.reportGroups.filter((g) => g.isActive && g.itemReportingGroupTypeId === 1 && g.sageItemReportingGroupId != null);
  }

  promptSetDefaultGroup(): void {
    this.selectedDefaultGroupId = this.defaultSalesGroupId;
    this.showSetDefaultGroupPrompt = true;
  }

  cancelSetDefaultGroup(): void {
    this.showSetDefaultGroupPrompt = false;
    this.maybeStartTour();
  }

  confirmSetDefaultGroup(): void {
    if (!this.selectedDefaultGroupId || this.savingDefaultGroup) return;
    this.savingDefaultGroup = true;
    this.sageService.setDefaultSalesReportingGroup(this.selectedDefaultGroupId).subscribe({
      next: () => {
        this.savingDefaultGroup = false;
        this.defaultSalesGroupId = this.selectedDefaultGroupId;
        this.showSetDefaultGroupPrompt = false;
        this.toast.success('Sales group saved. Items without their own group will use it.');
        this.loadErrorGroups();
        this.maybeStartTour();
      },
      error: (err) => {
        this.savingDefaultGroup = false;
        // A 400 says why the group was refused, in words written for users.
        this.toast.error((err?.status === 400 && err?.error?.message) || sageErrorMessageFor(err) || 'Could not save the sales group. Please try again.');
      },
    });
  }

  // ---- First-time walkthrough ----

  // Waits for the dashboard rather than pulling a user off the page they opened.
  private maybeStartTour(): void {
    if (this.tourOpen || this.tourShownThisVisit) return;
    if (this.showSetDefaultGroupPrompt || this.showDisconnectConfirm) return;
    if (this.loadError || this.activeTab !== 'sync' || this.entityStatuses.length === 0) return;
    if (!this.tourKey || readTourStatus(this.tourKey) !== null) return;
    this.openTour();
  }

  openTour(): void {
    if (this.DRILL_DOWN_TABS.has(this.activeTab)) {
      this.backToDashboard();
    } else if (this.activeTab !== 'sync') {
      this.selectTab('sync');
    }
    this.tourSteps = consoleTourSteps(this.entityStatuses.map((e) => e.entityType));
    this.tourOfferHint = !!this.tourKey && readTourStatus(this.tourKey) === null;
    this.tourShownThisVisit = true;
    this.helpPulse = false;
    this.tourOpen = true;
  }

  onTourOutcome(status: SageTourStatus): void {
    writeTourStatus(this.tourKey, status);
    this.helpPulse = this.tourOfferHint;
  }

  onTourClosed(): void {
    this.tourOpen = false;
    this.helpPulse = false;
    this.maybePromptDefaultGroup();
  }

  // ---- Connection actions ----

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (this.menuOpen) {
      const btn = document.getElementById('consoleMenuBtn');
      const menu = document.getElementById('consoleMenu');
      if (!(target && (btn?.contains(target) || menu?.contains(target)))) {
        this.menuOpen = false;
      }
    }

    if (this.openProvenanceItemId !== null && !target?.closest('.prov-cell')) {
      this.openProvenanceItemId = null;
    }
  }

  disconnectSage(): void {
    this.menuOpen = false;
    if (this.disconnecting) return;
    this.showDisconnectConfirm = true;
  }

  cancelDisconnect(): void {
    this.showDisconnectConfirm = false;
  }

  confirmDisconnect(): void {
    this.showDisconnectConfirm = false;
    this.disconnecting = true;
    this.sageService.disconnect().subscribe({
      next: () => {
        this.sageService.clearOverviewBundle();
        this.router.navigate(['/sage-integration']);
      },
      error: () => {
        this.disconnecting = false;
        this.toast.error('Could not disconnect Sage. Please try again.');
      },
    });
  }
}
